/**
 * Shared date-range helpers for the Smart-feature list pages
 * (Consumption Events, Restock Events, and anything else that needs a
 * Today / WTD / MTD / Last-7 / Last-30 / Custom selector).
 *
 * (2026-04-24, Jason Cox) Extracted from per-page duplicates in
 * DeliveryTickets.tsx / RestockEvents.tsx that were byte-identical.
 *
 * All dates are handled in local time and YMD-stringified -- the backend
 * range filters expect day-granular `YYYY-MM-DD` strings, not timestamps.
 */

export type RangePreset = 'today' | 'wtd' | 'mtd' | 'last7' | 'last30' | 'custom';

export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfWeekMonday(): Date {
  const d = today();
  // JS: Sunday=0, Monday=1, ..., Saturday=6
  const offset = d.getDay() === 0 ? 6 : d.getDay() - 1;
  d.setDate(d.getDate() - offset);
  return d;
}

function startOfMonth(): Date {
  const d = today();
  d.setDate(1);
  return d;
}

function daysAgo(n: number): Date {
  const d = today();
  d.setDate(d.getDate() - n);
  return d;
}

export function rangeForPreset(
  preset: RangePreset,
  customStart: string,
  customEnd: string,
): { startDate: string; endDate: string } {
  const end = toYmd(today());
  switch (preset) {
    case 'today':  return { startDate: end, endDate: end };
    case 'wtd':    return { startDate: toYmd(startOfWeekMonday()), endDate: end };
    case 'mtd':    return { startDate: toYmd(startOfMonth()), endDate: end };
    case 'last7':  return { startDate: toYmd(daysAgo(6)), endDate: end };
    case 'last30': return { startDate: toYmd(daysAgo(29)), endDate: end };
    case 'custom': return { startDate: customStart || end, endDate: customEnd || end };
  }
}

export const PRESET_LABELS: Record<RangePreset, string> = {
  today: 'Today',
  wtd: 'Week to date',
  mtd: 'Month to date',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  custom: 'Custom range',
};

/** Convenience: today as YMD (local). */
export function todayYmd(): string {
  return toYmd(today());
}
